import { useMemo } from "react";

import { useSelector } from "react-redux";

import { useFiltersConfigContext } from "../context/FiltersConfigContext";
import categoriesService from "../services/categories-service";
import filtersService from "../services/filters-service";
import type { FilterVisibilityParams } from "../services/filters-service/types";
import { selectIsFiltersCategoriesPanelExpanded, selectSelectedFilterCategory } from "../store/selectors";
import type { FilterCategory } from "../types/category";
import type { SearchFilterInstance } from "../types/searchFilterInstance";

import { useFilterListSearch } from "./useFilterListSearch";

type HookParams<TFilter extends SearchFilterInstance> = Pick<
    FilterVisibilityParams<TFilter>,
    "filtersConfig" | "selectedFilterKeys" | "dirtyFilterKeys"
>;

type HookValue<TFilter extends SearchFilterInstance, TKey extends string> = {
    readonly filterList: readonly TFilter[];
    readonly isSearchActive: boolean;
    // Exposed so a consumer can derive a second (e.g. collapsed) visible-filters view via
    // filtersService.getVisibleFilters without re-running the search.
    readonly searchMatchedFilters: readonly TFilter[] | undefined;
    readonly autoExpandFilterKey: TKey | null | undefined;
    readonly categoriesWithHiddenFilters: ReadonlySet<FilterCategory>;
};

export const useVisibleFilters = <
    TFilter extends SearchFilterInstance = SearchFilterInstance,
    TKey extends string = string,
>({
    filtersConfig,
    selectedFilterKeys,
    dirtyFilterKeys,
}: HookParams<TFilter>): HookValue<TFilter, TKey> => {
    const isCategoryPanelExpanded = useSelector(selectIsFiltersCategoriesPanelExpanded);
    const selectedCategory = useSelector(selectSelectedFilterCategory);

    const { filtersSearchConfigList } = useFiltersConfigContext<TFilter, TKey>();

    const { searchResults, isSearchActive } = useFilterListSearch<TFilter, TKey>({
        filtersConfig,
        searchConfigMaps: filtersSearchConfigList,
    });

    const visibleFilters = useMemo(
        () =>
            filtersService.getVisibleFilters(
                {
                    filtersConfig,
                    selectedFilterKeys,
                    dirtyFilterKeys,
                    searchMatchedFilters: searchResults?.matchedFilters,
                    isSearchActive,
                },
                {
                    isCategoryPanelExpanded,
                    selectedCategory,
                },
            ) as readonly TFilter[],
        [
            filtersConfig,
            selectedFilterKeys,
            dirtyFilterKeys,
            searchResults?.matchedFilters,
            isSearchActive,
            isCategoryPanelExpanded,
            selectedCategory,
        ],
    );

    const categoriesWithHiddenFilters = useMemo(
        () =>
            categoriesService.getCategoriesWithHiddenFilters({
                filtersList: filtersConfig,
                isFilterHidden: (filter: TFilter) =>
                    filtersService.isFilterHidden(filter, { selectedFilterKeys, dirtyFilterKeys }),
            }),
        [filtersConfig, selectedFilterKeys, dirtyFilterKeys],
    );

    return {
        filterList: visibleFilters,
        isSearchActive,
        searchMatchedFilters: searchResults?.matchedFilters,
        autoExpandFilterKey: searchResults?.autoExpandFilterKey,
        categoriesWithHiddenFilters,
    };
};
